// IntroPage.jsx
import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import IntroPageTable from "./IntroPageTable";
import toast from "react-hot-toast";
import CloseIcon from "@mui/icons-material/Close";
import ViewCarouselIcon from "@mui/icons-material/ViewCarousel";
import { axiosConfig } from "../../axios/axiosConfig";

const IntroPage = () => {
  const [data, setData] = useState([]);
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({ order: "", image_url: "" });
  const [editId, setEditId] = useState(null);

  // Fetch data
  const fetchData = async () => {
    try {
      const res = await axiosConfig({ method: "get", url: "/home/intro-page" });
      setData(res?.data?.response || []);
    } catch (error) {
      const errMsg =
        error?.response?.data?.message ||
        error.message ||
        "Error fetching data";
      console.error("Error fetching data:", errMsg);

      toast.error(errMsg);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Open dialog for Create
  const handleOpenCreate = () => {
    setFormData({ order: "", image_url: "" });
    setEditId(null);
    setOpen(true);
  };

  // Open dialog for Edit
  const handleOpenEdit = (row) => {
    setFormData({ order: row.order, image_url: row.image_url });
    setEditId(row.id);
    setOpen(true);
  };

  // Delete
  const handleDelete = async (id) => {
    try {
      const res = await axiosConfig({
        method: "delete",
        url: `/home/intro-page/${id}/delete/`,
      });
      const deleteMsg = res?.data?.message || "Deleted successfully";
      toast.success(deleteMsg);
      fetchData();
    } catch (error) {
      console.error("Delete failed:", error);
      const errMsg =
        error?.response?.data?.message || error.message || "Delete failed";
      toast.error(errMsg);
    }
  };

  // Submit Create or Update
  const handleSubmit = async () => {
    if (!formData.order || !formData.image_url) {
      toast.error("Both fields are required.");
      return;
    }
    try {
      if (editId) {
        // Update
        await axiosConfig({
          method: "post",
          url: `/home/intro-page/update/${editId}/`,
          data: formData,
        });
      } else {
        // Create
        await axiosConfig({
          method: "post",
          url: "/home/intro-page-create",
          data: formData,
        });
      }
      fetchData();
      setOpen(false);
    } catch (error) {
      console.error("Save failed:", error);
    }
  };

  return (
    <Box sx={{ p: 3, background: "#eef2f7", minHeight: "100vh" }}>
      {/* Page Header */}
      <Box
        sx={{
          background: "#04343a",
          borderRadius: "12px",
          boxShadow: "0 4px 20px rgba(4,52,58,0.3)",
          p: "20px 28px",
          mb: 3,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <ViewCarouselIcon sx={{ color: "#fff", fontSize: 32 }} />
          <Box>
            <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "20px" }}>
              Intro Screen
            </Typography>
            <Typography sx={{ color: "rgba(255,255,255,0.65)", fontSize: "13px" }}>
              Manage intro screen slides and order
            </Typography>
          </Box>
        </Box>
        <Button
          onClick={handleOpenCreate}
          sx={{
            background: "linear-gradient(135deg, #016551, #014d3d)",
            color: "#fff",
            fontWeight: 600,
            borderRadius: "8px",
            px: 3,
            boxShadow: "0 4px 12px rgba(1,101,81,0.3)",
            "&:hover": { background: "linear-gradient(135deg, #1db954 0%, #17a349 100%)" },
          }}
        >
          Add New
        </Button>
      </Box>

      {/* Table Card */}
      <Box
        sx={{
          background: "#fff",
          borderRadius: "12px",
          border: "1px solid #e2e8f0",
          boxShadow: "0 1px 6px rgba(0,0,0,0.07)",
          overflow: "hidden",
        }}
      >
        <IntroPageTable rows={data} onEdit={handleOpenEdit} onDelete={handleDelete} />
      </Box>

      {/* Dialog */}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: "12px" } }}
      >
        <DialogTitle sx={{ background: "#04343a", color: "#fff", fontWeight: 700, fontSize: "18px", display: "flex", alignItems: "center", justifyContent: "space-between", pb: 2 }}>
          {editId ? "Edit Intro Page" : "Add Intro Page"}
          <IconButton onClick={() => setOpen(false)} size="small" sx={{ color: "#fff" }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <TextField
            margin="dense"
            label="Order"
            type="number"
            fullWidth
            value={formData.order}
            onChange={(e) => setFormData({ ...formData, order: e.target.value })}
          />
          <TextField
            margin="dense"
            label="Image URL"
            fullWidth
            value={formData.image_url}
            onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpen(false)} sx={{ color: "#64748b", fontWeight: 600 }}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            sx={{
              background: "linear-gradient(135deg, #016551, #014d3d)",
              color: "#fff",
              fontWeight: 600,
              borderRadius: "8px",
              px: 4,
              "&:hover": { background: "linear-gradient(135deg, #1db954 0%, #17a349 100%)" },
            }}
          >
            {editId ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default IntroPage;
